import { useState } from 'react';
import { Mission } from '../../data/missions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import FileDrop from '../FileDrop/FileDrop';
import MissionShell, { ErrorNotice, InfoNotice } from '../MissionShell/MissionShell';
import { generateMaReport, generateMaReportExcel, MaReportExcelSummary, MaReportSummary } from '../../api/batch13Api';
import { useFileRun } from '../../hooks/useFileRun';
import type { FileResult } from '../../api/files';
import { ProcessingNotice } from '@/components/ui/processing-notice';

export type MaReportKind = 'standard' | 'monthly';

interface PanelProps {
  mission: Mission;
  kind: MaReportKind;
  onBack: () => void;
}

const textareaClass =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground';

function MaReportPanel({ mission, kind, onBack }: PanelProps) {
  const standard = kind === 'standard';
  const [pnl, setPnl] = useState<File[]>([]);
  const [bs, setBs] = useState<File[]>([]);
  const [coa, setCoa] = useState<File[]>([]);
  const [prior, setPrior] = useState<File[]>([]);
  const [logo, setLogo] = useState<File[]>([]);
  const [clientName, setClientName] = useState('');
  const [subClientName, setSubClientName] = useState('');
  const [notes, setNotes] = useState('');
  const [title, setTitle] = useState('Management Report');
  const [makePptx, setMakePptx] = useState(true);
  const { status, error, result, run } = useFileRun<FileResult<MaReportSummary>>();
  const [yearlyPnl, setYearlyPnl] = useState<File[]>([]);
  const [xlBs, setXlBs] = useState<File[]>([]);
  const [yeDate, setYeDate] = useState('');
  const xl = useFileRun<FileResult<MaReportExcelSummary>>();

  const steps = standard
    ? [
        'Upload the Profit & Loss and Balance Sheet Excel exports and the Chart of Accounts CSV.',
        'Prior-year comparison is optional: upload a prior-year Profit & Loss, or include it as a second sheet in the P&L file. An uploaded file takes priority.',
        'Click Generate Report. A PDF (and a PowerPoint, in a ZIP) downloads automatically.',
      ]
    : [
        'Upload the Profit & Loss and Balance Sheet Excel exports and the Chart of Accounts CSV.',
        'The Summary and P&L Analysis pages compare the current month with the prior month taken from the P&L file itself. A prior-year P&L is optional and only feeds the Last Year columns.',
        'Click Generate Report. The PDF downloads automatically.',
        'For the Excel report, use the section below: upload the full-year Profit & Loss (location columns + Total) and one Balance Sheet per location, pick the year-end date, then click Generate Excel.',
      ];

  const ready = Boolean(pnl[0] && coa[0] && bs[0]);

  const handleSubmit = async () => {
    if (!ready) return;
    await run(() => generateMaReport(kind, {
      pnl: pnl[0],
      bs: bs[0] ?? null,
      coa: coa[0],
      priorPnl: prior[0] ?? null,
      logo: logo[0] ?? null,
      reportTitle: title,
      clientName,
      subClientName,
      customNotes: notes,
      makePptx,
    }), 'Report generation failed.');
  };

  const handleExcel = async () => {
    if (!yearlyPnl[0]) return;
    await xl.run(() => generateMaReportExcel(yearlyPnl[0], xlBs, yeDate), 'Excel generation failed.');
  };

  return (
    <MissionShell mission={mission} onBack={onBack} steps={steps} wide>
      <div className="grid gap-x-4 sm:grid-cols-2">
        <FileDrop label="Profit & Loss (.xlsx)" required accept=".xlsx" hint="XLSX" accentColor={mission.color} files={pnl} onFiles={setPnl} />
        <FileDrop label="Chart of Accounts (.csv)" required accept=".csv" hint="CSV" accentColor={mission.color} files={coa} onFiles={setCoa} />
        <FileDrop label="Balance Sheet (.xlsx)" required accept=".xlsx" hint="XLSX" accentColor={mission.color} files={bs} onFiles={setBs} />
        <FileDrop
          label="Prior-year Profit & Loss (.xlsx, optional)"
          note="Leave empty to use a prior-year sheet inside the P&L file, if it has one."
          accept=".xlsx"
          hint="XLSX"
          accentColor={mission.color}
          files={prior}
          onFiles={setPrior}
        />
      </div>

      <div className="mb-5 rounded-lg border border-border bg-muted p-4">
        <p className="mb-3 text-sm font-bold text-foreground">🏷️ Client details</p>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="ma-client" className="mb-1.5">
              Client name
            </Label>
            <Input id="ma-client" value={clientName} onChange={(e) => setClientName(e.target.value)} />
            <p className="mt-1 text-xs text-muted-foreground">Leave blank to use the company name from the P&L file.</p>
          </div>
          <div>
            <Label htmlFor="ma-sub" className="mb-1.5">
              Sub-client name
            </Label>
            <Input id="ma-sub" value={subClientName} onChange={(e) => setSubClientName(e.target.value)} />
          </div>
        </div>
        <div className="mt-4">
          <FileDrop label="Logo (optional)" accept=".png,.jpg,.jpeg" hint="PNG or JPG" accentColor={mission.color} files={logo} onFiles={setLogo} />
        </div>
        <div className="mt-2">
          <Label htmlFor="ma-notes" className="mb-1.5">
            Notes to the Accounts
          </Label>
          <textarea id="ma-notes" rows={6} className={textareaClass} value={notes} onChange={(e) => setNotes(e.target.value)} />
          <p className="mt-1 text-xs text-muted-foreground">
            Type or paste the notes. They appear as the "Notes to the Accounts" page, with your line breaks kept.
          </p>
        </div>
        <div className="mt-4 grid items-end gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="ma-title" className="mb-1.5">
              Report title
            </Label>
            <Input id="ma-title" value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          {standard && (
            <label className="flex items-center gap-2 pb-2 text-sm text-foreground">
              <input type="checkbox" checked={makePptx} onChange={(e) => setMakePptx(e.target.checked)} />
              Also generate PowerPoint (downloaded as a ZIP with the PDF)
            </label>
          )}
        </div>
      </div>

      <Button
        onClick={handleSubmit}
        disabled={!ready || status === 'loading'}
        style={{ backgroundColor: mission.color }}
        className="w-full text-white hover:opacity-90"
      >
        {status === 'loading' ? 'Building report…' : '⚡ Generate Report'}
      </Button>
      {status === 'loading' && <ProcessingNotice label={'Building report…'} />}

      {status === 'error' && error && <ErrorNotice message={error} />}
      {status === 'success' && result && (
        <div className="mt-4 space-y-2 text-sm">
          <div className="rounded-lg bg-green-50 px-4 py-3 text-green-700">✅ Report generated. {result.filename} downloaded.</div>
          {result.summary?.prior_source ? (
            <div className="rounded-lg bg-blue-50 px-4 py-3 text-blue-700">
              Prior-year comparison taken from the {result.summary.prior_source}.
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">
              No prior-year data found, so the Last Year columns{standard ? ' / Var %' : ' and chart lines'} are blank.
            </p>
          )}
        </div>
      )}
      {status === 'idle' && !ready && (
        <InfoNotice>
          Upload the Profit &amp; Loss, Balance Sheet, and Chart of Accounts files to proceed.
        </InfoNotice>
      )}

      {!standard && (
        <div className="mt-8 border-t border-border pt-6">
          <p className="mb-1 text-base font-bold text-foreground">📊 Excel Report</p>
          <p className="mb-4 text-xs text-muted-foreground">
            The P&amp;L here is the full-year export by location: Account | one column per location | Total.
          </p>
          <div className="grid gap-x-4 sm:grid-cols-2">
            <FileDrop
              label="Full-year Profit & Loss (.xlsx) - location columns + Total"
              required
              accept=".xlsx"
              hint="XLSX"
              accentColor={mission.color}
              files={yearlyPnl}
              onFiles={setYearlyPnl}
            />
            <FileDrop
              label="Balance Sheets (.xlsx, one per location)"
              note="Each sheet is matched to a location by its 'Pub is ...' line."
              multiple
              accept=".xlsx"
              hint="XLSX"
              accentColor={mission.color}
              files={xlBs}
              onFiles={setXlBs}
            />
          </div>
          <div className="mb-5 max-w-xs">
            <Label htmlFor="ma-ye-date" className="mb-1.5">
              Year-end date (Mths column)
            </Label>
            <Input id="ma-ye-date" type="date" value={yeDate} onChange={(e) => setYeDate(e.target.value)} />
            <p className="mt-1 text-xs text-muted-foreground">Its month number goes in the Mths column, e.g. 31/03/2026 gives 3.</p>
          </div>
          <Button
            onClick={handleExcel}
            disabled={!yearlyPnl[0] || xl.status === 'loading'}
            style={{ backgroundColor: mission.color }}
            className="w-full text-white hover:opacity-90"
          >
            {xl.status === 'loading' ? 'Building Excel…' : '📊 Generate Excel'}
          </Button>
          {xl.status === 'loading' && <ProcessingNotice label={'Building Excel…'} />}
          {xl.status === 'error' && xl.error && <ErrorNotice message={xl.error} />}
          {xl.status === 'success' && xl.result && (
            <div className="mt-4 space-y-2 text-sm">
              <div className="rounded-lg bg-green-50 px-4 py-3 text-green-700">✅ Excel generated. {xl.result.filename} downloaded.</div>
              {xl.result.summary?.warnings?.map((w) => (
                <div key={w} className="rounded-lg bg-amber-50 px-4 py-3 text-amber-700">
                  {w}
                </div>
              ))}
            </div>
          )}
          {xl.status === 'idle' && !yearlyPnl[0] && <InfoNotice>Upload the Full-year Profit &amp; Loss to generate the Excel.</InfoNotice>}
        </div>
      )}
    </MissionShell>
  );
}

export default MaReportPanel;
